import React, { useEffect, useState } from 'react';

import Link from 'next/link';
import { useFetcherCSR } from '@/lib/useFetcherCSR';
import NextImage from '@/features/Image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { MdOutlineClose } from 'react-icons/md';
const BlogSidebar = ({
  recentPosts,
  onClose,
  isMobileOpen,
  isLoading,
}: {
  recentPosts: any;
  onClose: () => void;
  isMobileOpen: boolean;
  isLoading: boolean;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const category = searchParams?.get('category');

  const [activeCategorySlug, setActiveCategorySlug] = useState(category || '');
  // Changed to array for multiple tags
  const [activeTags, setActiveTags] = useState<string[]>([]);

  // Initialize tags from URL
  useEffect(() => {
    const tagParam = searchParams.get('tags');
    if (tagParam) {
      setActiveTags(tagParam.split(','));
    } else {
      setActiveTags([]);
    }
  }, [searchParams]);
  const { data: categories, isLoading: categoriesLoading } = useFetcherCSR(
    `/api/v2/blog/categories`
  );
  const { data: tags, isLoading: tagsLoading } = useFetcherCSR(
    `/api/v2/blog/trending/tags`
  );

  const handleActiveCategory = (slug: string) => {
    setActiveCategorySlug(slug);
  };

  const handleActiveTag = (tag: string) => {
    const newTags = activeTags.includes(tag)
      ? activeTags.filter((t) => t !== tag)
      : [...activeTags, tag];

    setActiveTags(newTags);

    // Create new URLSearchParams
    const params = new URLSearchParams();

    // Always maintain category if it exists
    if (activeCategorySlug) {
      params.set('category', activeCategorySlug);
    }

    // Only set tags if there are any
    if (newTags.length > 0) {
      params.set('tags', newTags.join(','));
    }

    // Use replace instead of push to avoid adding to history stack
    router.replace(`${pathname}?${params.toString()}`);
  };

  // Generate tag parameter string for URL

  return (
    <>
      <div className="sidebar" data-aos="fade-left" data-aos-duration={1000}>
        <div className="row g-4">
          <div className="col-12">
            <div className="sidebar__category">
              <div className="sidebar__head">
                <h3 className="fw-medium">Categories</h3>
                <button
                  className={`sidebar-close  ${isMobileOpen ? 'd-block' : 'd-none'}`}
                  onClick={onClose}
                >
                  <MdOutlineClose size={28} />
                </button>
              </div>
              <div className="sidebar__category-body">
                <div className="sidebar__category-content">
                  {categoriesLoading ? (
                    <ul>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <li
                          key={index}
                          className="d-flex justify-content-between align-items-center mb-2"
                        >
                          <span
                            className="placeholder-glow rounded"
                            style={{
                              height: '1.5rem',
                              width: '65%',
                              backgroundColor: '#e9ecef',
                            }}
                          ></span>
                          <span
                            className="placeholder-glow rounded"
                            style={{
                              height: '1rem',
                              width: '15%',
                              backgroundColor: '#e9ecef',
                            }}
                          ></span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul>
                      {categories?.data?.map((category: any) => (
                        <li
                          key={category?.id}
                          onClick={() => handleActiveCategory(category?.slug)}
                          className={`${category?.slug == activeCategorySlug ? 'active text-danger' : ''}`}
                          style={{ cursor: 'pointer' }}
                        >
                          <Link
                            href={{
                              pathname: '/blogs',
                              query: {
                                ...(category?.slug
                                  ? { category: category.slug }
                                  : {}),
                                ...(activeTags.length > 0
                                  ? { tags: activeTags.join(',') }
                                  : {}),
                              },
                            }}
                          >
                            {category?.name}
                          </Link>
                          <span>
                            {category?.counts?.toString()?.padStart(2, '0')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="sidebar__tags">
              <div className="sidebar__head">
                <h3 className="fw-medium">Popular Tags</h3>
              </div>
              <div className="sidebar__tags-body">
                <div className="tags">
                  {tagsLoading ? (
                    <ul className="gap-2">
                      {Array.from({ length: 9 }).map((_, index) => (
                        <li key={index} className="mb-2">
                          <div
                            className="placeholder-glow rounded"
                            style={{
                              height: '2rem',
                              width: '4rem',
                              backgroundColor: '#e9ecef',
                            }}
                          ></div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul>
                      {tags?.map((tagItem: any, index: number) => (
                        <li
                          onClick={() => handleActiveTag(tagItem)}
                          key={index}
                        >
                          <a
                            className={
                              activeTags.includes(tagItem) ? 'active' : ''
                            }
                            style={{ cursor: 'pointer' }}
                          >
                            {tagItem}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="sidebar__recentpost">
              <div className="sidebar__head">
                <h3 className="fw-medium">Recent posts</h3>
              </div>
              <div className="sidebar__recentpost-body">
                {isLoading ? (
                  <ul className="gap-2">
                    {Array.from({ length: 9 }).map((_, index) => (
                      <li key={index} className="mb-2">
                        <div
                          className="placeholder-glow rounded"
                          style={{
                            height: '6rem',
                            width: '100%',
                            backgroundColor: '#e9ecef',
                          }}
                        ></div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul>
                    {recentPosts?.slice(0, 5)?.map((post: any) => (
                      <li key={post?.id}>
                        <div className="sidebar__recentpost-item">
                          <div className="sidebar__recentpost-inner flex-nowrap">
                            <div className="sidebar__recentpost-thumb">
                              <Link href={post?.slug || ''}>
                                <NextImage
                                  width={post?.picture?.[0]?.width || 500}
                                  height={post?.picture?.[0]?.height || 500}
                                  src={post?.picture?.[0]?.url}
                                  alt="recentpost-image"
                                />
                              </Link>
                            </div>
                            <div className="sidebar__recentpost-content">
                              <p>
                                <Link href={`/blog/${post?.slug}`}>
                                  {post?.title}
                                </Link>
                              </p>
                              <span>{post?.date}</span>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
          {/* <div className="col-12">
              <div className="sidebar__sale-banner">
                <p>Big sale this week</p>
                <h4 className="fw-medium">
                  Exquisite Fruit Medley Package. Taste the Tropical Bliss in
                  Every mouthwatering Bite!
                </h4>
                <Link href="/shop" className="trk-btn trk-btn--primary">
                  Shop Now
                </Link>
              </div>
            </div> */}
        </div>
      </div>
    </>
  );
};

export default BlogSidebar;
